// src/pages/ResetPassword.js (v2 – config-driven, clean)

import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import "../styles/ResetPassword.css";

/**
 * Password reset page.
 *
 * Validates 6-digit code and sets new password.
 *
 * @component
 * @returns {JSX.Element}
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefillEmail = query.get("email") || "";

  const [email, setEmail] = useState(prefillEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await api.post(API_PATHS.RESET_PASSWORD, {
        email: email.trim(),
        code: code.trim(),
        password,
      });

      setNotice("Password reset successfully. Redirecting to login…");
      setTimeout(() => navigate(ROUTES.LOGIN), 2000);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Invalid or expired code. Please request a new one.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-container page">
      <div className="reset-card">
        <header className="reset-header">
          <h1 className="reset-title">Reset your password</h1>
          <p className="reset-subtitle">
            Enter the 6-digit code we emailed you and choose a new password.
          </p>
        </header>

        {notice && (
          <p className="success-message" role="status" aria-live="polite">
            {notice}
          </p>
        )}
        {error && (
          <p className="error-message" role="alert" aria-live="assertive">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="reset-form">
          <div className="reset-field-group">
            <label htmlFor="reset-email" className="reset-label">
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              className="reset-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="you@school.ie"
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-code" className="reset-label">
              Reset code
            </label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              className="reset-input"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              placeholder="123456"
              required
              disabled={loading}
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-password" className="reset-label">
              New password
            </label>
            <input
              id="reset-password"
              type="password"
              className="reset-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={loading}
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-confirm" className="reset-label">
              Confirm new password
            </label>
            <input
              id="reset-confirm"
              type="password"
              className="reset-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="primary-btn reset-primary-btn"
            disabled={loading}
          >
            {loading ? "Resetting…" : "Reset password"}
          </button>
        </form>

        <div className="reset-resend">
          <button
            type="button"
            className="link-btn reset-link-btn"
            onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
            disabled={loading}
          >
            Didn&apos;t get a code? Resend
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;